// Copied from course-platform, this is the final particle effect we create in Module 1.
import * as React from 'react';
import { styled } from '@linaria/react';

import { range, mix, random, normalize } from '@/utils';
import useToggle from '@/hooks/use-toggle';

import VisuallyHidden from '@/components/VisuallyHidden';

import PopCircle from './PopCircle';

const MIN_DISTANCE = 32;
const MAX_DISTANCE = 64;
const PARTICLE_DELAY = 150;

type Particle = {
  id: string;
  angle: number;
  distance: number;
  fadeDuration: number;
  fadeDelay: number;
  popDuration: number;
  size: number;
  createdAt: number;
  startHue: number;
  hueShift: number;
  lightness: number;
  twinkleOpacity: number;
  twinkleDuration: number;
};

interface Props {
  chaos: number;
  twinkleIntensity: number;
  numOfParticles?: number;
}

function FinalParticleButton({
  chaos,
  twinkleIntensity,
  numOfParticles,
}: Props) {
  const [isLiked, toggleLiked] = useToggle(false);
  const [particles, setParticles] = React.useState<Particle[]>([]);

  // Ok, so, before iOS 17, Safari had a bug with partial keyframes. This button combines a fade-out animation with a twinkle animation, and on iOS 16 and earlier, the particles become fully opaque after fading out, which is very janky.
  // I have no idea how to feature-query this, since it's more of a bug than a feature. But, the “hyphens: auto” feature was introduced with iOS 17, and has been supported in all other browsers for a long time, so I can use that as a stand-in.
  // When `legacyMode` is true, I’ll render an extra wrapping <div> to avoid the issue, at the expense of some performance.
  const [legacyMode, setLegacyMode] = React.useState(false);

  React.useEffect(() => {
    const supportsHyphens = window.CSS?.supports?.('hyphens', 'auto');

    if (!supportsHyphens) {
      setLegacyMode(true);
    }
  }, []);

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      const now = Date.now();

      setParticles((currentParticles) =>
        currentParticles.filter((particle) => {
          const timeSinceCreation = now - particle.createdAt;
          return timeSinceCreation < 4000;
        })
      );
    }, 4000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [particles]);

  function handleClick() {
    if (isLiked) {
      toggleLiked(false);
      return;
    }
    toggleLiked(true);

    window.setTimeout(() => {
      setParticles([
        ...particles,
        ...generateParticles(chaos, numOfParticles),
      ]);
    }, PARTICLE_DELAY);
  }

  return (
    <Button onClick={handleClick}>
      <PopCircle isPopped={isLiked} />

      {particles.map((particle) =>
        legacyMode ? (
          <LegacyTwinkleWrapper
            key={particle.id}
            style={{
              '--angle': particle.angle + 'deg',
              '--distance': particle.distance + 'px',
              '--size': particle.size + 'px',
              '--pop-duration': particle.popDuration + 'ms',
              '--twinkle-opacity': normalize(
                twinkleIntensity,
                0,
                1,
                1,
                particle.twinkleOpacity
              ),
              '--twinkle-duration': particle.twinkleDuration + 'ms',
            }}
          >
            <LegacyParticle
              style={{
                '--fade-duration': particle.fadeDuration + 'ms',
                '--fade-delay': particle.fadeDelay + 'ms',
                '--from-hue': particle.startHue + 'deg',
                '--to-hue': particle.hueShift + 'deg',
                '--lightness': particle.lightness + '%',
              }}
            />
          </LegacyTwinkleWrapper>
        ) : (
          <Particle
            key={particle.id}
            style={{
              '--angle': particle.angle + 'deg',
              '--distance': particle.distance + 'px',
              '--size': particle.size + 'px',
              '--pop-duration': particle.popDuration + 'ms',
              '--fade-duration': particle.fadeDuration + 'ms',
              '--fade-delay': particle.fadeDelay + 'ms',
              '--from-hue': particle.startHue + 'deg',
              '--to-hue': particle.hueShift + 'deg',
              '--lightness': particle.lightness + '%',
              '--twinkle-opacity': normalize(
                twinkleIntensity,
                0,
                1,
                1,
                particle.twinkleOpacity
              ),
              '--twinkle-duration': particle.twinkleDuration + 'ms',
            }}
          />
        )
      )}
      <Svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <HeartPath
          d="M3.68546 5.43796C8.61936 1.29159 11.8685 7.4309 12.0406 7.4309C12.2126 7.43091 15.4617 1.29159 20.3956 5.43796C26.8941 10.8991 13.5 21.8215 12.0406 21.8215C10.5811 21.8215 -2.81297 10.8991 3.68546 5.43796Z"
          data-liked={isLiked}
        />
      </Svg>
      <VisuallyHidden>Like this post</VisuallyHidden>
    </Button>
  );
}

function generateParticles(
  chaos: number,
  numOfParticles?: number
): Array<Particle> {
  numOfParticles = numOfParticles ?? normalize(chaos, 0, 1, 12, 30);

  return range(numOfParticles).map((index) => {
    const symmetricalAngle = normalize(
      index,
      0,
      numOfParticles,
      0,
      360
    );
    const angleJitter = chaos * 20;
    const angle =
      symmetricalAngle + random(-angleJitter, angleJitter);

    const distance = mix(
      index % 2 === 0 ? 45 : 55,
      random(MIN_DISTANCE, MAX_DISTANCE),
      chaos
    );

    const popDurationJitter = chaos * 1000;
    // `popDuration` is based on the distance each particle travels, but it has significant jitter at high chaos. I’m using Math.max() to ensure that the duration is at least 200ms, since it’s possible for negative durations otherwise.
    const popDuration = Math.max(
      normalize(distance, MIN_DISTANCE, MAX_DISTANCE, 500, 1000) +
        random(-popDurationJitter, popDurationJitter),
      200
    );

    const fadeDurationJitter = chaos * 300;
    const fadeDuration =
      popDuration +
      random(-fadeDurationJitter, fadeDurationJitter) +
      900;
    const fadeDelay = mix(300, random(0, 1000), chaos);

    const startHue = random(0, 360);
    const hueShift = random(180, 720);
    const lightness = normalize(chaos, 0, 1, 100, 70);
    const twinkleOpacity = 0;
    const twinkleDuration = random(200, 500);

    return {
      id: crypto.randomUUID(),
      angle,
      distance,
      size: mix(12, random(6, 20), chaos),
      createdAt: Date.now(),
      popDuration,
      fadeDuration,
      fadeDelay,
      startHue,
      hueShift,
      lightness,
      twinkleOpacity,
      twinkleDuration,
    };
  });
}

const Button = styled.button`
  --particle-curve: cubic-bezier(0.2, 0.56, 0, 1);

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  width: 96px;
  height: 96px;
  margin: auto;
  cursor: pointer;
  border-radius: 50%;
  &:hover {
    background: hsl(0deg 0% 100% / 0.1);
  }
`;

const Svg = styled.svg`
  --red: hsl(350deg 100% 50%);
  position: relative;
  width: 48px;
  height: 48px;
  overflow: visible;
`;

const HeartPath = styled.path`
  stroke: white;
  stroke-width: 2;
  stroke-linecap: round;
  transform-origin: center;

  ${Button}:hover & {
    stroke: var(--red);
  }

  &[data-liked='true'] {
    stroke: var(--red);
    fill: var(--red);
    /* prettier-ignore */
    animation:
      fromShrunken 2000ms 250ms backwards linear(0, 0.04 1.1%, 0.156 2.3%, 1.015 8.5%, 1.157 10.4%, 1.217 12.4%, 1.217 13.6%, 1.193 15%, 0.992 21.7%, 0.964 23.5%, 0.952 25.3%, 0.957 27.9%, 1.002 34.7%, 1.01 38.2%, 0.998 51%, 1);
  }
`;

const LegacyTwinkleWrapper = styled.span`
  position: absolute;
  inset: 0;
  margin: auto;
  width: var(--size);
  height: var(--size);
  transform: translate(
    calc(cos(var(--angle)) * var(--distance)),
    calc(sin(var(--angle)) * var(--distance))
  );
  pointer-events: none;

  @keyframes twinkle {
    from {
      opacity: var(--twinkle-opacity);
    }
    to {
      opacity: 1;
    }
  }

  animation:
    twinkle var(--twinkle-duration) ease-in-out infinite alternate,
    fromRestingPosition var(--pop-duration) var(--particle-curve);
`;

const LegacyParticle = styled.span`
  display: block;
  position: relative;
  width: inherit;
  height: inherit;
  border-radius: 50%;
  background-color: hsl(var(--from-hue) 100% var(--lightness));
  animation:
    hueShift 1000ms forwards linear,
    fadeToTransparent var(--fade-duration) var(--fade-delay) forwards;
`;

const Particle = styled.span`
  position: absolute;
  inset: 0;
  margin: auto;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  background-color: hsl(var(--from-hue) 100% var(--lightness));
  transform: translate(
    calc(cos(var(--angle)) * var(--distance)),
    calc(sin(var(--angle)) * var(--distance))
  );
  pointer-events: none;

  @keyframes twinkle {
    from {
      opacity: var(--twinkle-opacity);
    }
    to {
      opacity: 1;
    }
  }

  animation:
    hueShift 1000ms forwards linear,
    twinkle var(--twinkle-duration) ease-in-out infinite alternate,
    fadeToTransparent var(--fade-duration) var(--fade-delay) forwards,
    fromRestingPosition var(--pop-duration) var(--particle-curve);
`;

export default FinalParticleButton;
